import {
  Button,
  Form,
  Space,
  Table,
  Image,
  Popconfirm,
  TablePaginationConfig,
} from 'antd';
import { ColumnsType } from 'antd/es/table';
import { CmsApi, CmsSpecialContent } from 'apis/cms';
import LOCALS from 'commons/locals';
import i18n from 'i18n';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Trans } from 'react-i18next';
import CmsSpecialContentEditModal from './edit-modal';

const CmsSpecialContentPage = () => {
  const [form] = Form.useForm<{
    keyword?: string;
  }>();
  const [loading, setLoading] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(false);
  const [selectData, setSelectData] = useState<CmsSpecialContent>();
  const [specialContentList, setSpecialContentList] = useState<
    CmsSpecialContent[]
  >([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  // 用于存储当前分页信息的ref，避免在事件处理函数中形成闭包
  const paginationRef = useRef(pagination);
  useEffect(() => {
    paginationRef.current = pagination;
  }, [pagination]);

  const getList = useCallback((pageNum = 1, pageSize = 10) => {
    setLoading(true);
    CmsApi.getSpecialContentsList({ pageNum, pageSize })
      .then((res) => {
        setSpecialContentList(res.list);
        setPagination((prev) => ({
          ...prev,
          current: pageNum,
          pageSize,
          total: res.total,
        }));
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const onFinish = useCallback(() => {
    getList(1, paginationRef.current.pageSize);
  }, [getList]);

  // 只在组件挂载时加载一次数据
  useEffect(() => {
    getList(1, 10);
  }, [getList]);

  const handleTableChange = useCallback(
    (page: TablePaginationConfig) => {
      getList(page.current, page.pageSize);
    },
    [getList],
  );

  const columns: ColumnsType<CmsSpecialContent> = useMemo(() => {
    return [
      {
        title: 'ID',
        dataIndex: 'id',
        key: 'id',
        width: '5%',
      },
      {
        title: i18n.t('product_name'),
        dataIndex: 'productName',
        key: 'productName',
        width: '15%',
      },
      {
        title: i18n.t('rank'),
        dataIndex: 'rank',
        key: 'rank',
        width: '8%',
      },
      {
        title: i18n.t('product_pictures'),
        dataIndex: 'pic',
        key: 'pic',
        width: '15%',
        render(pic?: string) {
          if (!pic) return '-';

          // 处理多图显示 - 只显示第一张图片
          const images = pic.split(',').filter(Boolean);
          if (images.length === 0) return '-';

          // 只显示第一张图片，不显示图片数量
          return <Image src={images[0]} alt="" height={80} />;
        },
      },
      {
        title: i18n.t('associated_product_id'),
        dataIndex: 'productId',
        key: 'productId',
        width: '8%',
        render(productId?: number) {
          return productId === null ? 'null' : productId;
        },
      },
      {
        title: i18n.t('BjbkDXQZio'),
        dataIndex: 'arrivalDate',
        key: 'arrivalDate',
        width: '12%',
        render(arrivalDate?: string) {
          return arrivalDate || '-';
        },
      },
      {
        title: i18n.t('path'),
        dataIndex: 'path',
        key: 'path',
        width: '12%',
        render(path?: string) {
          return path || '-';
        },
      },
      {
        title: i18n.t('stamp'),
        dataIndex: 'stamp',
        key: 'stamp',
        width: '8%',
      },
      {
        title: i18n.t('description'),
        dataIndex: 'description',
        key: 'description',
        width: '20%',
        ellipsis: true,
      },
      {
        key: 'options',
        title: <Trans i18nKey={LOCALS.options} />,
        fixed: 'right',
        width: '11%',
        render: (data: CmsSpecialContent) => {
          return (
            <div>
              <Button
                type="link"
                onClick={() => {
                  setSelectData(data);
                  setOpen(true);
                }}
              >
                <Trans i18nKey={LOCALS.edit} />
              </Button>

              <Popconfirm
                title={i18n.t('delete')}
                description={
                  <div className="w-[150px]">
                    {i18n.t('are_you_sure_to_delete')}
                  </div>
                }
                onConfirm={async () => {
                  await CmsApi.deleteSpecialContent(data.id);
                  const { current, pageSize } = paginationRef.current;
                  getList(current, pageSize);
                }}
              >
                <Button type="link" danger>
                  <Trans i18nKey={LOCALS.delete} />
                </Button>
              </Popconfirm>
            </div>
          );
        },
      },
    ];
  }, [getList]);

  return (
    <div>
      <div>
        <Form form={form} onFinish={onFinish} layout="inline">
          <Form.Item>
            <Space>
              <Button
                onClick={() => {
                  setSelectData(undefined);
                  setOpen(true);
                }}
              >
                <Trans i18nKey={LOCALS.add} />
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </div>
      <Table
        tableLayout="fixed"
        bordered
        loading={loading}
        rowKey={'id'}
        style={{
          marginTop: 12,
        }}
        dataSource={specialContentList}
        columns={columns}
        pagination={pagination}
        onChange={handleTableChange}
      />

      {open && (
        <CmsSpecialContentEditModal
          open={open}
          onClose={() => setOpen(false)}
          data={selectData}
          onLoad={() => {
            const { current, pageSize } = paginationRef.current;
            getList(current, pageSize);
          }}
        />
      )}
    </div>
  );
};

export default CmsSpecialContentPage;
